import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { axiosInstance } from './axios';
import type {
  RecommendedProductsDTOType,
  RecommendedFacetsDTOType,
  RecommendedFacetDTOType,
  RecommendationRequestPayloadType,
} from '@admin-panel/types';

export type RecommendationsResponse = {
  products: RecommendedProductsDTOType;
  facets: RecommendedFacetsDTOType;
  found?: number;
  hasMore?: boolean;
};

class SearchStylesApiClient {
  protected baseURL: string;

  constructor() {
    this.baseURL = `${GATEWAY_API_URL}/search/styles`;
  }

  async search(
    payload: RecommendationRequestPayloadType,
    options?: { headers?: Record<string, string>; signal?: AbortSignal },
  ): Promise<RecommendationsResponse> {
    try {
      const response = await axiosInstance.post(this.baseURL, payload, {
        headers: options?.headers,
        signal: options?.signal,
      });

      const data = response.data.data;

      // Transform facets - sort and organize them
      const transformedFacets = data.facets
        .sort((a: RecommendedFacetDTOType, b: RecommendedFacetDTOType) => {
          if (a.field_name === 'In-Stock Facilities') return -1;
          if (b.field_name === 'In-Stock Facilities') return 1;
          return a.field_name.localeCompare(b.field_name);
        })
        .map((f: RecommendedFacetDTOType) => ({
          field_name: f.field_name,
          counts: f.counts?.sort(
            (a: { value: string; count: number }, b: { value: string; count: number }) => {
              if (b.count === a.count) {
                return a.value.localeCompare(b.value);
              }
              return b.count - a.count;
            },
          ),
        }));

      return {
        products: data.products,
        facets: transformedFacets,
        found: data?.found,
        hasMore: data?.hasMore,
      };
    } catch (error) {
      console.error('Error searching styles:', error);
      throw error;
    }
  }

  async typeahead(query: string): Promise<{ products: any }> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/typeahead`, {
        params: { q: query.trim() },
      });

      return {
        products: response.data.data.products,
      };
    } catch (error) {
      console.error('Error in typeahead search:', error);
      throw error;
    }
  }
}

export default SearchStylesApiClient;
